// Bill due date from the vendor's payment terms (Stephanie 2026-10-20: "due
// date from vendor terms"). Vendors' terms are free text on the vendor record
// ("Net 30", "net 45 days", "2% 10 Net 30", "Due on receipt", "credit card").
// Only clear terms give a date — anything else leaves it blank for a person
// to set (never a guess).

export type TermsDue =
  | { kind: "net"; days: number; label: string }
  | { kind: "on_receipt"; label: string }
  | { kind: "unknown"; label: string | null };

/** Pure: what the vendor's terms mean for a due date. */
export function parseVendorTerms(terms: string | null | undefined): TermsDue {
  const t = (terms ?? "").trim();
  if (!t) return { kind: "unknown", label: null };
  const net = /\bnet\s*-?\s*(\d{1,3})\b/i.exec(t);
  if (net) {
    const days = Number(net[1]);
    if (days >= 0 && days <= 180) return { kind: "net", days, label: `Net ${days}` };
  }
  if (/\b(due\s+(on|upon)\s+receipt|upon\s+receipt|on\s+receipt|c\.?o\.?d\.?|net\s*0)\b/i.test(t)) {
    return { kind: "on_receipt", label: "Due on receipt" };
  }
  return { kind: "unknown", label: t };
}

/** Pure: the due date (YYYY-MM-DD) for a bill dated `billDate`, or null when
 *  the terms don't say. Calendar days, no time zones. */
export function dueDateFromTerms(billDate: string, terms: string | null | undefined): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(billDate)) return null;
  const p = parseVendorTerms(terms);
  if (p.kind === "unknown") return null;
  const days = p.kind === "net" ? p.days : 0;
  const [y, m, d] = billDate.split("-").map(Number) as [number, number, number];
  const dt = new Date(Date.UTC(y, m - 1, d + days));
  return dt.toISOString().slice(0, 10);
}
